<script lang="ts" setup>
  import Button from '@leaflink/stash/Button.vue';
  import Divider from '@leaflink/stash/Divider.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { addMonths, format } from 'date-fns';
  import { computed, onMounted, ref } from 'vue';
  import { I18nT } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import { useFeatureEnrollmentProvider } from '@/components/FeatureEnrollment/FeatureEnrollmentLayout.vue';
  import { DATE_FORMATS, MARKETING_URLS, SUBSCRIPTION_PLAN_FREE_TRIAL_PERIOD_MONTHS } from '@/constants';
  import { INVENTORY_INSIGHTS_SUCCESS_MODAL_STORAGE_KEY } from '@/constants/storage';
  import { FeatureEnrollmentPostRequest } from '@/models/featureEnrollment';
  import { PosSystemName, PosSystemOptions } from '@/models/inventoryInsights';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { useFeatureEnrollmentPost } from '@/queries/feature-enrollment/feature-enrollment.query';
  import { trackWithSegment } from '@/segment';
  import { switchCompany } from '@/services/api/company';
  import { formatDateTime } from '@/utils/dates';
  import formatAddress from '@/utils/formatAddress';
  import { getBillingPaymentMethodInfo } from '@/utils/getBillingPaymentMethodInfo';
  import { money } from '@/utils/i18n';

  import { useInventoryInsightsProvider } from '../InventoryInsights.vue';

  const router = useRouter();

  const {
    company,
    productName,
    order,
    preventNativeDialog,
    isNueIntegrationEnabled,
    selectedPosSystem,
    posCredentials,
    enrollmentPrice,
    paymentMethods,
  } = useInventoryInsightsProvider();

  const { fetchUser } = useUserStore();

  const trialTypes = {
    Days: 'day',
    Months: 'month',
    Years: 'year',
  };

  const { mutateAsync } = useFeatureEnrollmentPost({
    onError() {
      isProcessing.value = false;
      isNavForwardDisabled.value = false;
    },
    async onSuccess() {
      preventNativeDialog.value = true;

      trackWithSegment({
        event: 'inventoryInsightsEnrollmentCompleted',
        properties: {
          pos_system: selectedPosSystem.value,
          product_name: productName,
          element_engaged: 'Activate Button',
          page_engaged_from: currentStep.value?.text,
        },
      });

      window.sessionStorage.setItem(INVENTORY_INSIGHTS_SUCCESS_MODAL_STORAGE_KEY, company.slug);

      if (company.id) {
        await switchCompany({ companyId: company.id });
      }

      await fetchUser();

      router.push(`/c/${company.slug}/`);
    },
  });

  const { enrollButtonText, isProcessing, isNavForwardDisabled, currentStep, back } = useFeatureEnrollmentProvider({
    events: {
      onEnroll() {
        isProcessing.value = true;
        isNavForwardDisabled.value = true;

        mutateAsync({
          organizationId: null,
          premiumFeature: 'catman',
          companiesToEnroll: companiesToEnroll.value,
        });
      },
    },
  });

  const defaultPaymentMethod = ref<ReturnType<typeof getBillingPaymentMethodInfo>>();

  const defaultPaymentMethodLabel = computed(() => {
    if (!defaultPaymentMethod.value) return '';

    const { name, flag } = defaultPaymentMethod.value;

    return name + (flag && ': ' + flag);
  });

  type PosSystemSettings = {
    settingName: string;
    settingValue: string;
  };

  const getSettings = (systemName: PosSystemOptions) => {
    const settings: Record<Exclude<PosSystemName, 'other'>, PosSystemSettings[]> = {
      dutchie: [{ settingName: 'api_key', settingValue: posCredentials.dutchie.apiKey || '' }],
      flowhub: [
        { settingName: 'api_key', settingValue: posCredentials.flowhub.apiKey || '' },
        {
          settingName: 'client_id',
          settingValue: posCredentials.flowhub.clientId || '',
        },
        { settingName: 'location_id', settingValue: posCredentials.flowhub.locationId || '' },
      ],
    };
    return settings[systemName];
  };

  const companiesToEnroll = computed<FeatureEnrollmentPostRequest['companiesToEnroll']>(() => {
    const { id } = company;

    const integrationSettings = getSettings(selectedPosSystem.value);

    return [
      {
        companyId: id || null,
        orderId: order.value?.id,
        configuration: {
          integratedSystemName: selectedPosSystem.value,
          integrationSettings,
        },
        freeTrialDisplayed: true,
        priceDisplayed:
          order.value?.totalPrice && isNueIntegrationEnabled.value
            ? money(order.value?.totalPrice)
            : money(enrollmentPrice.value),
        freeTrialDurationDisplayed:
          order.value?.freeTrialUnit && isNueIntegrationEnabled.value
            ? `${order.value?.freeTrialUnit} ${order.value?.freeTrialType}`
            : null,
      },
    ];
  });

  enrollButtonText.value = i18n.t('inventoryInsights.startFreeTrial');

  const displayPrice = computed(() => {
    if (order.value?.totalPrice && isNueIntegrationEnabled.value) {
      return money(order.value?.totalPrice);
    } else {
      return money(enrollmentPrice.value);
    }
  });

  const paymentDueDate = computed(() => {
    if (isNueIntegrationEnabled.value && order.value?.firstPaymentDueDate) {
      return formatDateTime(order.value.firstPaymentDueDate, DATE_FORMATS.MONTH_LONG);
    }
    return format(addMonths(new Date(), SUBSCRIPTION_PLAN_FREE_TRIAL_PERIOD_MONTHS), DATE_FORMATS.MONTH_LONG);
  });

  const handleTosLinkClick = () => {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentInfoClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'Terms of Service link',
        page_engaged_from: currentStep.value?.text || '',
      },
    });
  };

  const handleDefaultPaymentMethod = () => {
    const defaultMethod = paymentMethods.value.find((method) => method.isDefault);

    if (defaultMethod) {
      defaultPaymentMethod.value = getBillingPaymentMethodInfo({ method: defaultMethod });
    }
  };

  onMounted(() => {
    handleDefaultPaymentMethod();
  });
</script>

<template>
  <h3 class="tw-py-3">
    {{ i18n.t('inventoryInsights.leaflinkPlusMonthlySubscription') }}
  </h3>

  <Divider />

  <div class="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-y-6 lg:tw-grid-cols-[auto_35%_15%]" data-test="locations">
    <div class="lg:tw-pr-12">
      <h4>
        <strong>{{ company.name }}</strong>
      </h4>
      <p v-if="company" class="tw-m-0 tw-mt-2 tw-flex tw-gap-1">
        <Icon name="location" dense />
        {{ company.address && formatAddress({ company: company.address }) }}
      </p>
    </div>

    <div class="lg:tw-pr-12">
      <h4 class="tw-font-semibold">{{ i18n.t('inventoryInsights.posProvider') }}</h4>
      <p class="tw-m-0 tw-mt-2 tw-truncate tw-text-sm">{{ i18n.t(`inventoryInsights.${selectedPosSystem}`) }}</p>
    </div>

    <div class="tw-self-center tw-text-base tw-font-semibold tw-text-ice-900 lg:tw-text-right" data-test="price">
      {{ displayPrice }}
      {{ i18n.t('common.perMonth') }}
    </div>
  </div>

  <template v-if="defaultPaymentMethod">
    <h3 class="tw-mt-12 tw-flex tw-items-center tw-justify-between tw-py-3">
      {{ i18n.t('common.paymentMethod') }}
      <Button class="-tw-m-3" icon href="#" :title="i18n.t('common.editPaymentMethod')" @click="back">
        <Icon name="edit" />
      </Button>
    </h3>

    <Divider />

    <div class="tw-mt-6">
      <div class="tw-font-semibold tw-text-ice-900">{{ defaultPaymentMethodLabel }}</div>
      <div class="tw-mt-2">{{ defaultPaymentMethod?.truncatedNumber }}</div>
    </div>
  </template>

  <h3 class="tw-mt-12 tw-py-3">{{ i18n.t('common.planDetails') }}</h3>

  <Divider />

  <div class="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-y-6 lg:tw-grid-cols-[auto_50%]">
    <div class="lg:tw-pr-12">
      <template v-if="isNueIntegrationEnabled">
        <h4 v-if="!order?.freeTrialUnit">
          {{ i18n.t('inventoryInsights.nueIntegration.title') }}
        </h4>
        <h4 v-else>
          {{
            i18n.t('inventoryInsights.nueIntegration.freeTrial.title', {
              unit: order?.freeTrialUnit,
              type: order ? trialTypes[order?.freeTrialType] : '',
            })
          }}
        </h4>
        <I18nT
          :keypath="
            order?.freeTrialUnit
              ? 'inventoryInsights.nueIntegration.freeTrial.description'
              : 'inventoryInsights.nueIntegration.description'
          "
          scope="global"
          tag="p"
          class="tw-mt-2 tw-text-xs"
        >
          <template #link>
            <a
              :href="MARKETING_URLS.TERMS"
              class="tw-text-blue-500 tw-no-underline"
              target="_blank"
              @click="handleTosLinkClick"
            >
              {{ i18n.t('inventoryInsights.activateYourFreeTrial.link') }}
            </a>
          </template>
        </I18nT>
      </template>
      <template v-else>
        <h4>{{ i18n.t('inventoryInsights.activateYourFreeTrial.title') }}</h4>

        <I18nT
          keypath="inventoryInsights.activateYourFreeTrial.description"
          scope="global"
          tag="p"
          class="tw-mt-2 tw-text-xs"
        >
          <template #link>
            <a
              :href="MARKETING_URLS.TERMS"
              class="tw-text-blue-500 tw-no-underline"
              target="_blank"
              @click="handleTosLinkClick"
            >
              {{ i18n.t('inventoryInsights.activateYourFreeTrial.link') }}
            </a>
          </template>
        </I18nT>
      </template>
    </div>

    <div data-test="plan-details">
      <div
        class="tw-flex tw-items-center tw-justify-between tw-text-base tw-font-medium tw-text-ice-900"
        data-test="due-today"
      >
        <span>{{ i18n.t('inventoryInsights.dueToday') }}</span>
        <span>{{ !order?.freeTrialUnit ? displayPrice : '$0' }}</span>
      </div>

      <div
        class="tw-mt-6 tw-flex tw-items-center tw-justify-between tw-border-t tw-pt-6 tw-text-2xl tw-font-bold tw-text-ice-900"
      >
        <span>{{ i18n.t('common.total') }}</span>
        <!-- REMOVE TERNARY OPERATOR AFTER NUE INTEGRATION IS FULLY ENABLED-->
        <span>
          {{ displayPrice }}
          {{ i18n.t('common.perMonth') }}
        </span>
      </div>

      <p class="tw-mb-0 tw-mt-2 tw-text-right">
        {{ i18n.t('inventoryInsights.firstPaymentDue') }}
        {{ paymentDueDate }}
      </p>
    </div>
  </div>
</template>
